import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { isActiveBillingIdentity, type BillingIdentityLink } from '@/lib/billing/billing-identity';
import { resolveEntitlement } from '@/lib/billing/entitlement';
import { logPlanResolution } from '@/lib/billing/resolve-plan';
import type { PlanContextResponse } from '@/lib/billing/plan-context-shared';
import { getPlanConfig, type PlanTier } from '@/lib/planLimits';
import { getEntitlements } from '@/lib/plan-enforcement';

export type { PlanTier } from '@/lib/planLimits';

function billingIdentityForUser(
  billingIdentity: BillingIdentityLink | null,
  authUserId: string | undefined
) {
  if (!authUserId || !billingIdentity?.user_id || billingIdentity.user_id !== authUserId) {
    return null;
  }
  return billingIdentity;
}

/**
 * The fresh, per-request `plan-context` API response is always authoritative once it has
 * loaded — it reflects the current Stripe/DB state. `localTier` (derived from AuthContext's
 * cached profile/billingIdentity React state) is only used as a pre-fetch placeholder so the
 * UI doesn't flash "Free" before the first plan-context response arrives. Never let a stale
 * cached non-free tier override a fresh API response, in either direction (upgrade or downgrade).
 */
export function tierFromContext(
  localTier: PlanTier,
  planContext: PlanContextResponse | null
): PlanTier {
  if (!planContext) return localTier;
  return (planContext.tier as PlanTier | undefined) ?? 'free';
}

export function usePlan() {
  const { user, profile, billingIdentity, refreshProfile } = useAuth();
  const scopedBilling = billingIdentityForUser(billingIdentity, user?.id);
  const [planContext, setPlanContext] = useState<PlanContextResponse | null>(null);
  const [planLoading, setPlanLoading] = useState(!!user?.id);
  const [planActivating, setPlanActivating] = useState(false);

  const localEntitlement = resolveEntitlement(profile, scopedBilling);
  const tier = tierFromContext(localEntitlement.tier, planContext);
  const source =
    localEntitlement.tier !== 'free'
      ? localEntitlement.source
      : (planContext?.source ?? localEntitlement.source);
  const config = getPlanConfig(tier);
  const entitlements = getEntitlements(tier);
  const hasActiveBillingIdentity = isActiveBillingIdentity(scopedBilling);

  const fetchPlanContext = useCallback(async () => {
    if (!user?.id) {
      setPlanContext(null);
      setPlanLoading(false);
      return null;
    }
    setPlanLoading(true);
    try {
      const res = await fetch('/api/billing/plan-context', {
        credentials: 'same-origin',
        cache: 'no-store',
      });
      const body = (await res.json()) as PlanContextResponse;
      if (res.ok) {
        setPlanContext(body);
        return body;
      }
      return null;
    } finally {
      setPlanLoading(false);
    }
  }, [user?.id]);

  const refreshPlanStatus = useCallback(
    async (withStripeSync = true) => {
      if (!user?.id) return null;
      setPlanActivating(true);
      try {
        if (withStripeSync) {
          await fetch('/api/billing/sync-from-stripe', {
            method: 'POST',
            credentials: 'same-origin',
            cache: 'no-store',
          }).catch(() => null);
        }
        const body = await fetchPlanContext();
        if (body && body.tier !== 'free') {
          await refreshProfile();
        }
        return body;
      } finally {
        setPlanActivating(false);
      }
    },
    [user?.id, fetchPlanContext, refreshProfile]
  );

  useEffect(() => {
    void fetchPlanContext();
  }, [fetchPlanContext]);

  useEffect(() => {
    if (!user?.id) return;
    logPlanResolution({
      authUserId: user.id,
      billingIdentityRow: scopedBilling,
      resolvedPlan: tier,
      resolvedSubscriptionStatus: localEntitlement.status,
      source: `usePlan:${source}`,
    });
  }, [user?.id, scopedBilling, tier, localEntitlement.status, source]);

  const planPending = planLoading || planActivating;

  return {
    tier,
    config,
    entitlements,
    billingIdentity: scopedBilling,
    hasActiveBillingIdentity,
    planContext,
    planLoading,
    planActivating,
    planPending,
    source,
    refreshPlanStatus,
    fetchPlanContext,

    isFree: !planPending && tier === 'free',
    isVerified: tier === 'verified' || tier === 'legacy',
    isLegacy: tier === 'legacy',
    isInstitutionPaid:
      tier === 'collective_verified' ||
      tier === 'collective_pro' ||
      tier === 'institution_starter' ||
      tier === 'institution_growth',
    isGrowth: tier === 'collective_pro' || tier === 'institution_growth',

    maxRecords: config.maxRecords,
    maxAchievements: config.maxRecords,
    storageLimitBytes: config.storageLimitBytes,
    storageMB: entitlements.maxStorageMb,
    storageGB:
      config.storageLimitBytes > 0
        ? Math.round(config.storageLimitBytes / (1024 * 1024 * 1024))
        : 0,

    canUseStoryHub: true,
    canAccessAnalytics: config.canAccessAnalytics,
    canUseAdvancedAnalytics: config.canUseAdvancedAnalytics,
    canAccessActivityLog: config.canAccessActivityLog,
    canShareLinks: config.canShareLinks,
    canUseCommunity: true,
    allPillarsVerified: config.allPillarsVerified,
    verifiedBadge: config.verifiedBadge,
    legacyProfile: tier === 'legacy',
    featuredBoosts: config.featuredBoosts,
    canUploadCredentials:
      config.canUploadCredentials || (planContext?.verificationCredits ?? 0) > 0,
    canRequestVerification:
      config.canRequestVerification || (planContext?.verificationCredits ?? 0) > 0,
    verificationCredits: planContext?.verificationCredits ?? 0,
    canUseJourneyMap: config.canUseJourneyMap,
    canUseGeospatialMap: config.canUseGeospatial,
    canUseFamilyLedger: config.canUseFamilyLedger,
    canUseTimeCapsule: config.canUseTimeCapsule,
    canUploadPhotos: config.canUploadPhotos,
    canUploadVoice: config.canUploadVoice,
    canUploadVideo: config.canUploadVideo,
    canUploadDocuments: config.canUploadDocuments,
    canUploadPodcasts: config.canUploadPodcasts,
    canGenerateProofCards: config.canGenerateProofCards,
    canUseInstitutionDashboard: config.canUseInstitutionDashboard,

    maxVerificationsPerMonth: config.maxVerificationsPerMonth,
    canUseBulkVerification: config.canUseBulkVerification,
    canUseAPI: config.canUseAPI,
    institutionAnalytics: config.canAccessAnalytics && config.canUseInstitutionDashboard,
    canUseStoryLedger: config.canUseStoryLedger,
    canUseVerificationInbox: config.canUseVerificationInbox,
    canUseHeritageArchive: config.canUseHeritageArchive,
    canUseGeospatial: config.canUseGeospatial,
  };
}
